/**
 * HorizontalWizardStepper
 *
 * A horizontal step indicator for multi-step wizards.
 * Shows step numbers with visual indicators for status:
 * - pending: number in neutral circle
 * - active: number in brand/highlighted circle
 * - completed: checkmark in success circle
 * - skipped: dash in neutral circle
 */

import React from 'react';
import { FeatherCheck, FeatherMinus } from '@subframe/core';

export type StepStatus = 'pending' | 'active' | 'completed' | 'skipped';

export interface WizardStep {
  number: number;
  title: string;
}

export interface HorizontalWizardStepperProps {
  steps: WizardStep[];
  currentStep: number;
  stepStatus: Record<number, StepStatus>;
  onStepClick?: (step: number) => void;
}

export const HorizontalWizardStepper: React.FC<HorizontalWizardStepperProps> = ({
  steps,
  currentStep,
  stepStatus,
  onStepClick,
}) => {
  const getStepClasses = (step: WizardStep): string => {
    const status = stepStatus[step.number] || 'pending';
    const isActive = step.number === currentStep;

    if (isActive) {
      return 'bg-brand-500 text-white';
    }

    switch (status) {
      case 'completed':
        return 'bg-success-500 text-white';
      case 'skipped':
        return 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400';
      default:
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border border-neutral-border';
    }
  };

  const getLineClasses = (stepIndex: number): string => {
    const nextStep = steps[stepIndex + 1];
    if (!nextStep) return '';

    const currentStatus = stepStatus[steps[stepIndex].number] || 'pending';

    if (currentStatus === 'completed') {
      return 'bg-success-500';
    }

    return 'bg-neutral-border';
  };

  const renderStepContent = (step: WizardStep): React.ReactNode => {
    const status = stepStatus[step.number] || 'pending';
    const isActive = step.number === currentStep;

    if (isActive) {
      return step.number;
    }

    switch (status) {
      case 'completed':
        return <FeatherCheck className="w-4 h-4" />;
      case 'skipped':
        return <FeatherMinus className="w-4 h-4" />;
      default:
        return step.number;
    }
  };

  const handleClick = (step: WizardStep) => {
    if (onStepClick && step.number !== currentStep) {
      // Only allow clicking on completed or skipped steps to go back
      const status = stepStatus[step.number] || 'pending';
      if (status === 'completed' || status === 'skipped' || step.number < currentStep) {
        onStepClick(step.number);
      }
    }
  };

  return (
    <div className="flex items-center gap-2">
      {steps.map((step, index) => (
        <React.Fragment key={step.number}>
          <button
            type="button"
            onClick={() => handleClick(step)}
            disabled={!onStepClick}
            className={`flex items-center justify-center w-8 h-8 rounded-full text-caption font-caption transition-colors ${getStepClasses(
              step
            )} ${onStepClick ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
            title={step.title}
          >
            {renderStepContent(step)}
          </button>
          {index < steps.length - 1 && (
            <div className={`flex-1 h-0.5 ${getLineClasses(index)}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
